import path from 'path';
import { readdir, readFile } from 'fs/promises';
import Debug from '../../helpers/debug.js';
import ora from 'ora';
import axios from 'axios';
import AWS from 'aws-sdk';
import Table from 'cli-table';
import { getFolderFiles } from '../../helpers/utils.js';
import descriptions from '../../helpers/descriptions.js';
import { pr, pp, cl, themed, printJSON } from '../../helpers/print.js';
import { genCredentialsOnClientApi } from '../../helpers/api.js';

const debug = Debug('commands:snapshots:deploy');

const command = 'deploy';

const desc = 'Deploy Snapshots';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('tenant', { ...descriptions.options['tenant'].config, default: 'default' })
    .option('sourceFolder', descriptions.options['sourceFolder'].config)
    .demandOption('sourceFolder', descriptions.options['sourceFolder'].demandText);
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, tenant, sourceFolder } = argv;
  const { clientApiBaseUri } = hg;

  let message;
  let spinner = ora();
  const folderPrefix = path.resolve(process.cwd(), sourceFolder);
  try {
    message = themed(`Deploying Snapshots for Tenant ${themed(tenant, 'info')} to ${themed(env, 'info')}`);
    !json && spinner.info(themed(`Info: ${message}.`, 'secondary'));
    // 1. build list of deployable files
    const deployableFiles = await getFolderFiles(folderPrefix, {
      recursive: true,
    });
    if (deployableFiles.length === 0) {
      json
        ? printJSON({ status: 'error', error: 'There are no files to deploy at the specified location!' })
        : spinner.fail(
            themed(`Error: ${themed('There are no files to deploy at the specified location!')}.`, 'secondary')
          );
      return;
    }

    // 2. get STS credentials
    message = themed(`Verifying user and authorizing`);
    const { accessKeyId, secretAccessKey, sessionToken } = await genCredentialsOnClientApi({
      debug,
      baseUri: clientApiBaseUri,
      task: 'snapshot-deploy',
      env,
      tenant,
      apikey,
    });
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // create authenticated S3 instance
    const s3 = new AWS.S3({
      accessKeyId,
      secretAccessKey,
      sessionToken,
    });

    // 3. verify tenant is valid
    message = themed(`Validating tenant exists`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    try {
      const { CommonPrefixes: tenantFolders } = await s3
        .listObjectsV2({ Bucket: env, Prefix: `${tenant}/`, Delimiter: '/' })
        .promise();
      if (tenantFolders.length === 0) throw new Error('Invalid tenant');
    } catch (err) {
      debug('err', err);
      throw new Error(`Tenant doesn't exist. Please check your tenant (-u) argument.`);
    }
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    const rootKey = `${tenant}/snapshots/`;
    const table = new Table({ head: ['File', 'Status'] });
    // 4. cleanup old files
    message = themed(`Preparing deployment target`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const { Contents } = await s3.listObjectsV2({ Bucket: env, Prefix: `${rootKey}` }).promise();
    !json && spinner.stop();
    debug('contents', Contents);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const filesToDelete = Contents.map((item) => ({ Key: item.Key }));
    if (filesToDelete.length > 0) {
      const delParams = {
        Bucket: env,
        Delete: { Objects: filesToDelete, Quiet: true },
      };
      await s3.deleteObjects(delParams).promise();
      filesToDelete.forEach(({ Key }) => table.push([Key.substring(rootKey.length), themed('Deleted', 'warn')]));
    }
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 5. upload new files
    for await (const file of deployableFiles) {
      const relativePath = path.relative(sourceFolder, file);
      const key = `${rootKey}${relativePath}`;

      message = themed(`Deploying file: ${themed(relativePath, 'info')}`);
      !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
      const readBuffer = await readFile(file);
      const params = {
        Bucket: env,
        Key: key,
        Body: readBuffer,
      };

      const res = await s3.putObject(params).promise();
      !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
      debug('s3-put-res', res);
      table.push([relativePath, themed('Deployed', 'info')]);
    }

    if (json) {
      printJSON({ status: 'success', deployedFiles: deployableFiles });
    } else {
      // print results
      cl(table.toString());
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err });
    } else {
      spinner.fail(themed(`Error: ${message}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
      if (err && err.response && err.response.data) {
        debug('response', err.response.data);
      }
    }
  }
};

export { command, desc, builder, handler };
